import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  FormContainer,
  FormSection,
  FloatingField,
  InputText,
  Select,
} from '../../components/aip';
import { getAdminUser, updateAdminUser, type AdminUser } from '../../services/manageNewApi';

// 権限選択肢
const ROLE_OPTIONS = [
  { value: '0', label: '一般' },
  { value: '1', label: '管理者' },
];

/**
 * ユーザ編集ページ
 */
export const AdminUserEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // データ状態
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フォーム状態
  const [adminNumber, setAdminNumber] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminEmailSub, setAdminEmailSub] = useState('');
  const [isAdmin, setIsAdmin] = useState('0');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const data = await getAdminUser(parseInt(id));
        setUser(data);

        // フォームに値をセット
        setAdminNumber(data.admin_number || '');
        setAdminName(data.admin_name || '');
        setAdminEmail(data.admin_email || '');
        setAdminEmailSub(data.admin_email_sub || '');
        setIsAdmin(String(data.is_admin));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // 保存処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    // バリデーション
    if (!adminName.trim()) {
      setError('ユーザ名は必須です');
      return;
    }
    if (!adminEmail.trim()) {
      setError('メールアドレスは必須です');
      return;
    }
    // パスワードは空なら変更しない、入力がある場合はバリデーション
    if (password) {
      if (password.length < 8) {
        setError('パスワードは8文字以上で入力してください');
        return;
      }
      if (password !== passwordConfirm) {
        setError('パスワードが一致しません');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      const payload: {
        admin_number?: string;
        admin_name?: string;
        admin_email?: string;
        admin_email_sub?: string;
        is_admin?: number;
        password?: string;
      } = {
        admin_number: adminNumber.trim() || undefined,
        admin_name: adminName.trim(),
        admin_email: adminEmail.trim(),
        admin_email_sub: adminEmailSub.trim() || undefined,
        is_admin: parseInt(isAdmin, 10),
      };

      // パスワードが入力されていれば含める
      if (password) {
        payload.password = password;
      }

      await updateAdminUser(parseInt(id), payload);

      navigate('/meeting/manage-new/admin-users');
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // ローディング表示
  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        読み込み中...
      </div>
    );
  }

  // ユーザが見つからない場合
  if (!user && !loading) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: 'var(--color-red50)',
          color: 'var(--color-alert)',
          borderRadius: '0.5rem',
        }}
      >
        ユーザが見つかりません
      </div>
    );
  }

  return (
    <>
      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--color-red50)',
            color: 'var(--color-alert)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <FormContainer onSubmit={handleSubmit}>
        <FormSection title="基本情報" layout="vertical">
          <FloatingField label="ユーザ名" htmlFor="admin_name" required>
            <InputText
              id="admin_name"
              name="admin_name"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              placeholder="ユーザ名"
            />
          </FloatingField>

          <FloatingField label="社員番号" htmlFor="admin_number">
            <InputText
              id="admin_number"
              name="admin_number"
              value={adminNumber}
              onChange={(e) => setAdminNumber(e.target.value)}
              placeholder="社員番号"
            />
          </FloatingField>

          <FloatingField label="メールアドレス（ログインID）" htmlFor="admin_email" required>
            <InputText
              id="admin_email"
              name="admin_email"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="メールアドレス"
            />
          </FloatingField>

          <FloatingField label="サブメールアドレス" htmlFor="admin_email_sub">
            <InputText
              id="admin_email_sub"
              name="admin_email_sub"
              type="email"
              value={adminEmailSub}
              onChange={(e) => setAdminEmailSub(e.target.value)}
              placeholder="サブメールアドレス"
            />
          </FloatingField>

          <FloatingField label="権限" htmlFor="is_admin" required>
            <Select
              id="is_admin"
              name="is_admin"
              value={isAdmin}
              onChange={(e) => setIsAdmin(e.target.value)}
              options={ROLE_OPTIONS}
            />
          </FloatingField>
        </FormSection>

        <FormSection title="パスワード変更" subheading="変更する場合のみ入力してください">
          <FloatingField label="新しいパスワード" htmlFor="password">
            <InputText
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8文字以上（空欄の場合は変更なし）"
            />
          </FloatingField>

          <FloatingField label="パスワード（確認）" htmlFor="password_confirm">
            <InputText
              id="password_confirm"
              name="password_confirm"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="パスワード再入力"
            />
          </FloatingField>
        </FormSection>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button
            buttonType="outline"
            size="md"
            type="button"
            onClick={() => navigate('/meeting/manage-new/admin-users')}
            disabled={saving}
          >
            キャンセル
          </Button>
          <Button buttonType="solid-fill" size="md" type="submit" disabled={saving}>
            {saving ? '更新中...' : '更新'}
          </Button>
        </div>
      </FormContainer>
    </>
  );
};

export default AdminUserEditPage;
